import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Iconify } from 'src/components/iconify';

interface ScannerLinkProps {
  type: 'transaction' | 'address';
  hash: string;
  chain?: 'ETHEREUM' | 'BITCOIN' | 'SOLANA' | 'TRON' | 'BSC' | 'POLYGON' | 'BITCOIN_CASH' | 'LITECOIN' | 'ROBINHOOD';
}

/**
 * @description Component to display a link to a blockchain explorer for a given transaction or address.
 *
 * The link is generated based on the type (transaction or address) and the blockchain (Ethereum, Bitcoin, Bitcoin Cash, or Solana).
 *
 * The component also includes a copy button to copy the transaction hash or address to the clipboard.
 *
 */
export const ScannerLink: React.FC<ScannerLinkProps> = ({ type, hash, chain = 'ETHEREUM' }) => {
  const [copied, setCopied] = useState(false);

  const isMainnet = import.meta.env.VITE_REACT_APP_STAGING !== 'true';

  const baseUrl = {
    BITCOIN: isMainnet ? 'https://mempool.space' : 'https://mempool.space/testnet',
    BITCOIN_CASH: isMainnet
      ? 'https://blockchair.com/bitcoin-cash'
      : 'https://blockchair.com/bitcoin-cash/testnet',
    LITECOIN: isMainnet
      ? 'https://blockchair.com/litecoin'
      : 'https://blockchair.com/litecoin/testnet',
    ETHEREUM: isMainnet ? 'https://etherscan.io' : 'https://sepolia.etherscan.io',
    SOLANA: isMainnet ? 'https://solscan.io' : 'https://solscan.io/',
    // TRON is mainnet-only in this deployment (single TRON_RPC, no testnet
    // split), so always link to mainnet tronscan even on the staging build.
    TRON: 'https://tronscan.org',
    BSC: isMainnet ? 'https://bscscan.com' : 'https://testnet.bscscan.com',
    POLYGON: isMainnet ? 'https://polygonscan.com' : 'https://amoy.polygonscan.com',
    ROBINHOOD: isMainnet
      ? 'https://robinhoodchain.blockscout.com'
      : 'https://explorer.testnet.chain.robinhood.com',
  }[chain];

  const path = type === 'transaction' ? 'tx' : 'address';
  const copyAriaLabel = type === 'transaction' ? 'copy transaction hash' : 'copy address';
  let devnetParam = '';
  if (chain === 'SOLANA' && !isMainnet) {
    devnetParam = '?cluster=devnet';
  }

  /**
   * @description Function to handle copying the string value to clipboard.
   */
  const handleCopy = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(hash);
      setCopied(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    if (copied) {
      timeoutId = setTimeout(() => {
        setCopied(false);
      }, 3000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [copied]);

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={hash}>
        <a
          href={`${baseUrl}/${path}/${hash}${devnetParam}`}
          target="_blank"
          rel="noreferrer"
          style={{
            textDecoration: 'none',
            color: '#ff9800',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Typography variant="body2" sx={{ display: 'inline' }}>
            {hash?.length > 15 ? `${hash.slice(0, 8)}...${hash.slice(-8)}` : hash}
          </Typography>
        </a>
      </Tooltip>

      {/* Copy Button */}
      <IconButton
        onClick={handleCopy}
        size="small"
        sx={{ padding: '2px' }}
        aria-label={copyAriaLabel}
      >
        {copied ? (
          <Iconify icon="material-symbols:check-rounded" sx={{ color: 'success.main' }} />
        ) : (
          <Iconify icon="material-symbols:file-copy-outline" sx={{ color: 'primary.main' }} />
        )}
      </IconButton>
    </Box>
  );
};

export function getScannerUrl(
  type: 'transaction' | 'address',
  hash: string,
  chain: 'ETHEREUM' | 'BITCOIN' | 'SOLANA' | 'TRON' | 'BSC' | 'POLYGON' | 'BITCOIN_CASH' | 'LITECOIN' | 'ROBINHOOD' = 'ETHEREUM'
): React.ReactNode {
  return <ScannerLink type={type} hash={hash} chain={chain} />;
}
